import { useThree } from '@react-three/fiber';
import { type RefObject, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { axialToWorld } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import type { Cell } from '@/game/sim';
import { type UrgentAlert, urgentPulses } from '@/game/view/notifications';
import { type EdgeInset, type EdgeWeights, edgePoint, edgeWeights } from './edgeGeometry';

const GROUND_Y = config.hexMap.tile.thickness;
/** Most arrows at once (the newest alerts). */
const MAX_ARROWS = 4;
/**
 * Kept clear for the HUD: the left column (w-60), the timer and one banner row
 * under it, and a little room at the right and bottom, so arrows sit at the
 * edge of the map view. Arrows are drawn above the HUD, so one that still
 * lands on a corner panel or the dock stays visible and clickable.
 */
const INSET: EdgeInset = { top: 128, right: 28, bottom: 28, left: 268 };

// Reused by the projection loop (no per-frame allocation).
const scratch = new THREE.Vector3();

/** Projects a map tile to normalised device coordinates into `out`; false without a camera. */
function project(camera: THREE.Camera | null, pos: Cell, out: THREE.Vector3): boolean {
  if (!camera) return false;
  const w = axialToWorld(pos, config.hexMap.tile.radius);
  out.set(w.x, GROUND_Y, w.z).project(camera);
  return true;
}

const SIDES: Array<{ side: keyof EdgeWeights; className: string; to: string }> = [
  { side: 'top', className: 'inset-x-0 top-0 h-[clamp(48px,12vh,120px)]', to: 'to bottom' },
  { side: 'bottom', className: 'inset-x-0 bottom-0 h-[clamp(48px,12vh,120px)]', to: 'to top' },
  { side: 'left', className: 'inset-y-0 left-0 w-[clamp(48px,9vw,140px)]', to: 'to right' },
  { side: 'right', className: 'inset-y-0 right-0 w-[clamp(48px,9vw,140px)]', to: 'to left' },
];

/**
 * Urgent alerts at the screen edges: a soft danger glow that pulses when one
 * comes in (stronger on the side it happened, relative to the view), faintly
 * staying while an attack goes on, and an arrow on the edge pointing at each
 * one out of view (a click goes there). Arrows go once their spot is on
 * screen or the alert ends. The glow never takes clicks; reduced motion
 * gets a plain fade instead of the pulse (index.css).
 */
export function EdgeAlerts({
  alerts,
  ongoing,
  cameraRef,
  onFocus,
}: {
  /** Urgent alerts now, newest first (`urgentAlerts`). */
  alerts: readonly UrgentAlert[];
  /** An attack is going on right now: the glow stays faintly. */
  ongoing: boolean;
  /** The 3D view's camera (set from inside the Canvas). */
  cameraRef: RefObject<THREE.Camera | null>;
  onFocus: (pos: Cell) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const arrowRefs = useRef(new Map<number, HTMLButtonElement>());
  const seenRef = useRef(new Map<number, number>());
  const [pulse, setPulse] = useState<{ id: number; weights: EdgeWeights } | null>(null);
  const shown = alerts.slice(0, MAX_ARROWS);

  // A new alert (or an attack flaring up again) pulses the glow towards it.
  useEffect(() => {
    const pulses = urgentPulses(seenRef.current, alerts);
    if (pulses.length === 0) return;
    const latest = pulses[0];
    const weights = project(cameraRef.current, latest.pos, scratch)
      ? edgeWeights(scratch.x, scratch.y)
      : edgeWeights(0, 0);
    setPulse((p) => ({ id: (p?.id ?? 0) + 1, weights }));
  }, [alerts, cameraRef]);

  // Arrows follow the camera every frame, written straight to their styles.
  const positionsRef = useRef(shown);
  positionsRef.current = shown;
  const active = shown.length > 0;
  useEffect(() => {
    if (!active) return;
    let frame = 0;
    const update = () => {
      frame = requestAnimationFrame(update);
      const el = containerRef.current;
      if (!el) return;
      const width = el.clientWidth;
      const height = el.clientHeight;
      for (const a of positionsRef.current) {
        const arrow = arrowRefs.current.get(a.id);
        if (!arrow) continue;
        if (!project(cameraRef.current, a.pos, scratch)) {
          arrow.style.visibility = 'hidden';
          continue;
        }
        // Behind the camera (can't happen with this camera, but be safe): flip.
        const flip = scratch.z > 1 ? -1 : 1;
        const p = edgePoint(scratch.x * flip, scratch.y * flip, width, height, INSET);
        arrow.style.visibility = p.onScreen ? 'hidden' : 'visible';
        arrow.style.transform = `translate(${p.x}px, ${p.y}px) translate(-50%, -50%)`;
        const glyph = arrow.firstElementChild as HTMLElement | null;
        if (glyph) glyph.style.transform = `rotate(${p.angle}rad)`;
      }
    };
    update();
    return () => cancelAnimationFrame(frame);
  }, [active, cameraRef]);

  return (
    <>
      <div className="pointer-events-none absolute inset-0 z-[6] overflow-hidden">
        {/* Faint glow while an attack goes on. */}
        <div
          className={`absolute inset-0 transition-opacity duration-1000 ${ongoing ? 'opacity-25' : 'opacity-0'}`}
        >
          {SIDES.map(({ side, className, to }) => (
            <div
              key={side}
              className={`absolute ${className}`}
              style={{
                background: `linear-gradient(${to}, var(--color-danger-glow), transparent)`,
              }}
            />
          ))}
        </div>
        {/* The pulse: remounted (key) for each new one so its animation replays. */}
        {pulse && (
          <div key={pulse.id} className="edge-glow-pulse absolute inset-0">
            {SIDES.map(({ side, className, to }) => (
              <div
                key={side}
                className={`absolute ${className}`}
                style={{
                  opacity: pulse.weights[side],
                  background: `linear-gradient(${to}, var(--color-danger-glow), transparent)`,
                }}
              />
            ))}
          </div>
        )}
      </div>
      {/* Arrows above the HUD panels (z-10) and the banners and dock (z-20). */}
      <div ref={containerRef} className="pointer-events-none absolute inset-0 z-30 overflow-hidden">
        {shown.map((a) => (
          <button
            key={a.id}
            ref={(el) => {
              if (el) arrowRefs.current.set(a.id, el);
              else arrowRefs.current.delete(a.id);
            }}
            type="button"
            aria-label={`Go to: ${a.text}`}
            title={`${a.text} — click to go there`}
            onClick={() => onFocus(a.pos)}
            style={{ visibility: 'hidden' }}
            className="pill pointer-events-auto absolute left-0 top-0 flex h-8 w-8 items-center justify-center border-danger/60 bg-danger-surface text-sm text-danger transition-colors hover:bg-danger/30"
          >
            <span className="block leading-none">➤</span>
          </button>
        ))}
      </div>
    </>
  );
}

/** Inside the Canvas: hands its camera to `cameraRef`, for EdgeAlerts' projections. */
export function CameraBridge({ cameraRef }: { cameraRef: RefObject<THREE.Camera | null> }) {
  const camera = useThree((s) => s.camera);
  useEffect(() => {
    cameraRef.current = camera;
    return () => {
      if (cameraRef.current === camera) cameraRef.current = null;
    };
  }, [camera, cameraRef]);
  return null;
}
